// [Project] > [<specialist> status] - one specialist's file on this project, at the
// depth an operator works it: its status, then its next actions and tracked dates.
// See docs/Y2026-Q3/design-one-block-per-specialist.md section C.
import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useProjectAgentStatus } from "../api";
import { AgentFeedbackSection } from "../components/AgentFeedbackSection";
import { BriefingFrame } from "../components/BriefingFrame";
import { useBriefingView } from "../utils/briefingView";
import { reviewAttrs } from "../utils/reviewState";
import { BuildingReadinessView } from "../components/BuildingReadinessView";
import { ContractsCard } from "../components/ContractsCard";
import { DocumentClausesCard } from "../components/DocumentClausesCard";
import { HistoryCard } from "../components/HistoryCard";
import { LandlordPackCard } from "../components/LandlordPackCard";
import { ReadyToBuildCard } from "../components/ReadyToBuild";
import { StateSectionCards } from "../components/StateSectionCards";
import { useCitations } from "../components/useCitations";
import {
  NextActionsCard,
  TrackedDatesCard,
} from "../components/NextActionCards";
import { SpecialistRowSections } from "../components/SpecialistRowSections";
import { useScrollToHash } from "../utils/useScrollToHash";
import { projectSpecialistPath } from "./learningAgentPaths";
import styles from "./ProjectSpecialistTab.module.css";

export const ProjectSpecialistTab = () => {
  const { projectId, agent = "" } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  return (
    <BriefingFrame projectId={decoded} level="specialist_details">
      <SpecialistDetails projectId={decoded} agent={agent} ownPage />
    </BriefingFrame>
  );
};

export const SpecialistDetails = ({
  projectId: decoded,
  agent,
  ownPage,
}: {
  projectId: string;
  agent: string;
  /** Today's tab, which adds the cards that load their own data: the agent's questions
   * and the contract stages. Off on a past briefing, which shows how it was, and on a
   * Portfolio card, where every open card would fetch its own. */
  ownPage: boolean;
}) => {
  const { version } = useBriefingView();
  const { data, isLoading, error } = useProjectAgentStatus(
    decoded || null,
    version,
  );
  useScrollToHash(!!data);
  const entry = data?.agents.find((a) => a.agent === agent);
  // One citation lookup over the History and the shown sections, not one per card
  // (principle 8).
  const citations = useCitations(
    useMemo(
      () =>
        entry
          ? [
              ...entry.timeline,
              ...entry.sections.map((s) => s.body),
              ...(entry.landlord_pack?.entries.map((e) => e.email) ?? []),
            ]
          : [],
      [entry],
    ),
  );

  if (isLoading) return <p className={styles.muted}>Loading its file…</p>;
  if (error) return <p className={styles.error}>Could not load its file.</p>;
  if (!data) return null;

  if (!entry) {
    return (
      <p className={styles.muted}>
        This agent does not work this project. Switch it on from Settings.
      </p>
    );
  }

  const row = entry.row;
  const actionHref = (id: string) =>
    `${projectSpecialistPath(decoded, agent)}#${id}`;
  // Every open decision, since nothing ties the row's options to one of them.
  const openDecisions = (entry.lists?.actions ?? [])
    .filter((a) => a.type === "decision" && !a.closed)
    .map((a) => ({ title: a.title, href: actionHref(a.action_id) }));
  const contracts =
    entry.keeps_contracts && ownPage && version === null ? (
      <ContractsCard projectId={decoded} />
    ) : null;
  const landlordPack = entry.landlord_pack ? (
    <LandlordPackCard history={entry.landlord_pack} citations={citations} />
  ) : null;
  const history = (
    <HistoryCard
      title={
        entry.keeps_readiness ? "History outside the three areas" : "History"
      }
      entries={entry.timeline}
      citations={citations}
    />
  );
  const sections = (
    <StateSectionCards
      sections={entry.sections}
      citations={citations}
      pastBriefing={version !== null}
    />
  );
  const listCards = (
    <div
      className={styles.lists}
      {...reviewAttrs("specialist_details", `${entry.label} next actions`, {
        agent,
      })}
    >
      <NextActionsCard agent={agent} projectId={decoded} data={data} />
      <TrackedDatesCard agent={agent} projectId={decoded} data={data} />
    </div>
  );
  return (
    <div className={styles.tab}>
      {/* Only text inside this anchor can be commented on (ReviewProvider). */}
      <div
        className={styles.tab}
        {...reviewAttrs("specialist_details", entry.label, { agent })}
      >
        {!row ? (
          <p className={styles.muted}>
            {entry.label} is switched on but has not written a status yet. The
            next scheduled pass fills this in.
          </p>
        ) : entry.keeps_readiness ? (
          <div className={styles.tab}>
            <BuildingReadinessView
              row={row}
              projectId={decoded}
              data={data}
              agent={agent}
            />
            {/* Each area keeps its own timeline; its file's holds what falls outside
                them, shown only once there is any. */}
            {entry.timeline.length > 0 && history}
          </div>
        ) : (
          <SpecialistRowSections
            row={row}
            keepsApplications={entry.keeps_applications}
            lists={{ data, agent, projectId: decoded }}
            decisions={openDecisions}
            readyToBuild={
              <ReadyToBuildCard agent={agent} projectId={decoded} data={data} />
            }
            belowReadyToBuild={
              <>
                {contracts}
                {sections}
              </>
            }
            history={history}
          >
            {listCards}
          </SpecialistRowSections>
        )}
        {!row && contracts}
        {!row && sections}
        {!row && listCards}
        {!row && entry.timeline.length > 0 && history}
        {/* Full width: the table needs the room. */}
        {landlordPack}
      </div>
      {/* Today's clause statuses: a past briefing kept none. Outside the anchor: a
          comment on a clause list lives with its file, never a project
          (design-clause-comments.md section 1.2). */}
      {ownPage && version === null && (
        <DocumentClausesCard projectId={decoded} agent={agent} />
      )}
      {citations.modal}
      {/* Outside the briefing's review anchors: an answer is not a comment on it. */}
      {ownPage && (
        <AgentFeedbackSection scope={{ project_id: decoded, agent }} showProjects={false} />
      )}
    </div>
  );
};
